import React from 'react';
import { Formik, FormikProps } from 'formik';
import { Form } from 'formik-antd';
import { Row, Divider } from 'antd';
import * as Yup from 'yup';
import cx from 'classnames';

import FormikEffect from 'components/formik/FormikEffect';
import { countryUSAid } from 'constants/index';
import AssignmentWorkLocationForm from './AssignmentWorkLocationForm';
import AssignmentBillLocationForm from './AssignmentBillLocationForm';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';
import { Address, Addresses, FormType } from '../AssignmentModal';

import style from './WorksiteLocation.module.less';

export interface MainLocation {
  country?: number;
  state?: number;
  internationalStateName?: string;
  city?: string;
  internationalCityName?: string;
  address?: string;
  zip?: string;
}

type Props = {
  formRef: any;
  onChange: (formik: FormikProps<WorksiteLocationFormValues>) => void;
  mainLocation: MainLocation;
  mainLocationLabel: string;
  initialValues?: WorksiteLocationFormValues;
  disabled?: boolean;
  addresses: Addresses;
  onAddressChange: (
    formType: FormType,
    field: keyof Address,
    value: string | undefined,
  ) => void;
};

export interface WorksiteLocationFormValues {
  work: {
    country?: number;
    locationSameAsMain: boolean;
    state?: number;
    internationalStateName?: string;
    city: string;
    internationalCityName?: string;
    address: string;
    zip: string;
  };
  billing: {
    country?: number;
    locationSameAsWork: boolean;
    state?: number;
    internationalStateName?: string;
    city: string;
    internationalCityName?: string;
    address: string;
    zip: string;
  };
}

const initialValidationSchema = {
  work: {
    locationSameAsMain: Yup.boolean(),
    country: Yup.number().nullable(),
    state: Yup.string()
      .nullable()
      .when('locationSameAsMain', {
        is: (locationSameAsMain: boolean) => !locationSameAsMain,
        then: Yup.string().trim(),
      }),
    internationalStateName: Yup.string()
      .nullable()
      .when('locationSameAsMain', {
        is: (locationSameAsMain: boolean) => !locationSameAsMain,
        then: Yup.string()
          .trim()
          .max(120, `State should be 120 characters maximum`),
      }),
    internationalCityName: Yup.string()
      .nullable()
      .when('locationSameAsMain', {
        is: (locationSameAsMain: boolean) => !locationSameAsMain,
        then: Yup.string()
          .trim()
          .max(120, 'City should be 120 characters maximum'),
      }),
    city: Yup.string().when('locationSameAsMain', {
      is: (locationSameAsMain: boolean) => !locationSameAsMain,
      then: Yup.string()
        .trim()
        .max(120, 'City should be 120 characters maximum'),
    }),

    address: Yup.string().when('locationSameAsMain', {
      is: (locationSameAsMain: boolean) => !locationSameAsMain,
      then: Yup.string()
        .trim()
        .max(120, 'Address should be 120 characters maximum'),
    }),

    zip: Yup.string().when('locationSameAsMain', {
      is: (locationSameAsMain: boolean) => !locationSameAsMain,
      then: Yup.string()
        .trim()
        .max(120, 'ZIP code should be 120 characters maximum'),
    }),
  },
  billing: {
    locationSameAsWork: Yup.boolean(),
    country: Yup.number().nullable(),
    state: Yup.string()
      .nullable()
      .when('locationSameAsWork', {
        is: (locationSameAsWork: boolean) => !locationSameAsWork,
        then: Yup.string().trim(),
      }),
    internationalStateName: Yup.string()
      .nullable()
      .when('locationSameAsWork', {
        is: (locationSameAsWork: boolean) => !locationSameAsWork,
        then: Yup.string()
          .trim()
          .max(120, `State should be 120 characters maximum`),
      }),
    internationalCityName: Yup.string()
      .nullable()
      .when('locationSameAsWork', {
        is: (locationSameAsWork: boolean) => !locationSameAsWork,
        then: Yup.string()
          .trim()
          .max(120, 'City should be 120 characters maximum'),
      }),
    city: Yup.string()
      .nullable()
      .when('locationSameAsWork', {
        is: (locationSameAsWork: boolean) => !locationSameAsWork,
        then: Yup.string()
          .trim()
          .max(120, 'City should be 120 characters maximum'),
      }),

    address: Yup.string()
      .nullable()
      .when('locationSameAsWork', {
        is: (locationSameAsWork: boolean) => !locationSameAsWork,
        then: Yup.string()
          .trim()
          .max(120, 'Address should be 120 characters maximum'),
      }),

    zip: Yup.string().when('locationSameAsWork', {
      is: (locationSameAsWork: boolean) => !locationSameAsWork,
      then: Yup.string()
        .trim()
        .max(120, 'ZIP code should be 120 characters maximum'),
    }),
  },
};

const getSubmitValidationSchema = (
  workerAddress?: string,
  billingAddress?: string,
) =>
  Yup.object({
    work: Yup.object({
      ...initialValidationSchema.work,
      country: Yup.number()
        .nullable()
        .when('locationSameAsMain', {
          is: (locationSameAsMain: boolean) => !locationSameAsMain,
          then: Yup.number().required('Field is required'),
        }),

      state: Yup.string()
        .nullable()
        .when(['locationSameAsMain', 'country'], {
          is: (locationSameAsMain: boolean, country: number) =>
            !locationSameAsMain && checkIsCountriesWithStatesDropdown(country),
          then: Yup.string().trim().required('Field is required'),
        }),

      internationalStateName: Yup.string()
        .nullable()
        .when(['locationSameAsMain', 'country'], {
          is: (locationSameAsMain: boolean, country: number) =>
            !locationSameAsMain && !checkIsCountriesWithStatesDropdown(country),
          then: Yup.string()
            .trim()
            .required('Field is required')
            .max(120, 'State should be 120 characters maximum'),
        }),

      internationalCityName: Yup.string()
        .nullable()
        .when(['locationSameAsMain', 'country'], {
          is: (locationSameAsMain: boolean, country: number) =>
            !locationSameAsMain && !checkIsCountriesWithStatesDropdown(country),
          then: Yup.string()
            .trim()
            .required('Field is required')
            .max(120, 'City should be 120 characters maximum'),
        }),

      city: Yup.string().when(['locationSameAsMain', 'country'], {
        is: (locationSameAsMain: boolean, country: number) =>
          !locationSameAsMain && checkIsCountriesWithStatesDropdown(country),
        then: Yup.string()
          .trim()
          .required('Field is required')
          .max(120, 'City should be 120 characters maximum'),
      }),

      address: Yup.string().when('locationSameAsMain', {
        is: (locationSameAsMain: boolean) => !locationSameAsMain,
        then: Yup.string()
          .trim()
          .required('Field is required')
          .max(120, 'Address should be 120 characters maximum'),
      }),
      zip: Yup.string().when('locationSameAsMain', {
        is: (locationSameAsMain: boolean) => !locationSameAsMain,
        then: Yup.string()
          .trim()
          .max(120, 'ZIP code should be 120 characters maximum'),
      }),
    }),
    billing: Yup.object({
      ...initialValidationSchema.billing,
      country: Yup.number()
        .nullable()
        .when('locationSameAsWork', {
          is: (locationSameAsMain: boolean) => !locationSameAsMain,
          then: Yup.number().required('Field is required'),
        }),

      state: Yup.string()
        .nullable()
        .when(['locationSameAsWork', 'country'], {
          is: (locationSameAsWork: boolean, country: number) =>
            !locationSameAsWork && checkIsCountriesWithStatesDropdown(country),
          then: Yup.string().trim().required('Field is required'),
        }),

      internationalStateName: Yup.string()
        .nullable()
        .when(['locationSameAsWork', 'country'], {
          is: (locationSameAsWork: boolean, country: number) =>
            !locationSameAsWork && !checkIsCountriesWithStatesDropdown(country),
          then: Yup.string()
            .trim()
            .required('Field is required')
            .max(120, 'State should be 120 characters maximum'),
        }),

      internationalCityName: Yup.string()
        .nullable()
        .when(['locationSameAsWork', 'country'], {
          is: (locationSameAsWork: boolean, country: number) =>
            !locationSameAsWork && !checkIsCountriesWithStatesDropdown(country),
          then: Yup.string()
            .trim()
            .required('Field is required')
            .max(120, 'City should be 120 characters maximum'),
        }),

      city: Yup.string()
        .nullable()
        .when(['locationSameAsWork', 'country'], {
          is: (locationSameAsWork: boolean, country: number) =>
            !locationSameAsWork && checkIsCountriesWithStatesDropdown(country),
          then: Yup.string()
            .trim()
            .required('Field is required')
            .max(120, 'City should be 120 characters maximum'),
        }),

      address: Yup.string()
        .nullable()
        .billingAndWorkerAddressSameCheck(workerAddress, billingAddress)
        .when('locationSameAsWork', {
          is: (locationSameAsWork: boolean) => {
            return !locationSameAsWork;
          },
          then: Yup.string()
            .trim()
            .required('Field is required')
            .max(120, 'Address should be 120 characters maximum'),
        }),
      zip: Yup.string().when('locationSameAsWork', {
        is: (locationSameAsWork: boolean) => !locationSameAsWork,
        then: Yup.string()
          .trim()
          .max(120, 'ZIP code should be 120 characters maximum'),
      }),
    }),
  });

export default React.memo((props: Props) => {
  const {
    formRef,
    mainLocation,
    mainLocationLabel,
    onChange,
    initialValues: predefinedInitialValues,
    disabled: isFormDisabled = false,
    addresses,
    onAddressChange,
  } = props;

  const initialValues: WorksiteLocationFormValues = {
    work: {
      country: countryUSAid,
      locationSameAsMain: false,
      internationalStateName: undefined,
      state: undefined,
      internationalCityName: '',
      city: '',
      address: '',
      zip: '',
    },
    billing: {
      country: countryUSAid,
      locationSameAsWork: false,
      internationalStateName: undefined,
      state: undefined,
      internationalCityName: '',
      city: '',
      address: '',
      zip: '',
    },
  };

  return (
    <Formik
      innerRef={formRef}
      enableReinitialize
      validationSchema={getSubmitValidationSchema(
        addresses.worker.address,
        addresses.billing.address,
      )}
      initialValues={predefinedInitialValues || initialValues}
      onSubmit={() => {}}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />
          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              {isFormDisabled && (
                <div className={cx('hidden-md-up', style.hintSm)}>
                  Please select a customer for this assignment from the dropdown
                  before updating further information.
                </div>
              )}
              <Row gutter={40}>
                <AssignmentWorkLocationForm
                  formik={formik}
                  mainLocation={mainLocation}
                  disabled={isFormDisabled}
                  mainLocationLabel={mainLocationLabel}
                />

                <Divider className="modal-divider after-form hidden-md-up" />

                <AssignmentBillLocationForm
                  formik={formik}
                  mainLocation={mainLocation}
                  disabled={isFormDisabled}
                  onAddressChange={onAddressChange}
                  addresses={addresses}
                />
              </Row>

              {isFormDisabled && (
                <ul className={cx('hidden-md-down icon-list', style.hintMd)}>
                  <li>
                    <span className="icon icon-error" />
                    <span>
                      Please select a customer for this assignment from the
                      dropdown before updating further information.
                    </span>
                  </li>
                </ul>
              )}
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
});
